// @flow
import * as React from 'react'
import type { StyledBaseProps } from '../../../..'
import { FormattedMessage, isMessageObject, type Message } from 'mz-intl'
import LoadingSpinner from '../LoadingSpinner'
import { useAppearance } from '../../utils/withPalette'
import { isNil } from '../../utils'
import {
  Container,
  Baseline,
  Label,
  InputWrapper,
  Errors,
  LoadingContainer,
  ClearButton,
  Content,
  InputWithControls,
  ArrowButton,
  PrefixWrapper,
  PostfixWrapper,
} from './elements'

// Types
export type Input = {
  value?: any,
  name?: string,
  onChange?: Function,
  onFocus?: Function,
  onBlur?: Function,
  onClick?: Function,
  onKeyDown?: Function,
}
export type Appearance =
  | 'material'
  | 'material-wo-baseline'
  | 'mobile-search'
  | 'rect'
  | 'compact'
  | 'btp-field'
  | 'btp-field-outlined'
export type Meta = {
  active?: boolean,
  asyncValidating?: boolean,
  loading?: boolean,
  error?: string | ?Message,
  warning?: string | ?Message,
  info?: string | ?Message,
  touched?: boolean,
  valid?: boolean,
  locked?: boolean,
  children?: { [key: string]: Meta },
  deepValid?: boolean,
  deepLoading?: boolean,
  deepTouched?: boolean,
  deepActive?: boolean,
}
export type Event = {
  target: { value: string },
  preventDefault: () => any,
}
export type InputRenderProps = {
  innerRef?: (?any) => any,
  input: Input,
  style: Styles,
  props: any,
  children?: React.Node,
}
export type Props = {
  onClickClear?: Function,
  renderPrefix?: Styles => React.Node,
  renderPostfix?: Styles => React.Node,
  renderInput?: (props: InputRenderProps) => React.Node,
  emptyValueComparator?: (value: ?any, emptyValue: ?any) => boolean,
  appearance?: Appearance,
  meta: Meta,
  input: Input,
  emptyValue?: any,
  placeholder?: any,
  label?: any,
  name?: string,
  id?: string,
  type?: string,
  disableFloatingLabel?: boolean,
  disabled?: boolean,
  selectable?: boolean,
  cleanable?: boolean,
  hideErrors?: boolean,
  name?: string,
  type?: string,
  innerRef?: Function,
  focusableRef?: (ref: ?React.Node) => any,
  containerRef?: Function,
  margin?: 'none' | 'normal' | 'dense',
  required?: boolean,
}
export type BaseStyles = {
  $hidden?: boolean,
  $appearance?: string,
  $haveLabel?: boolean,
  $focus?: boolean,
  $focused?: boolean,
  $nonEmpty?: boolean,
  $invalid?: boolean,
  $loading?: boolean,
  $disabled?: boolean,
  $disableFloatingLabel?: boolean,
  $alwaysShowPlaceholder?: boolean,
  $cleanable?: boolean,
  $hasPrefix?: boolean,
}
export type Styles = BaseStyles & StyledBaseProps

// Utils
const preventDefault = e => e.preventDefault()

const getTranslatedValue = (val: any) =>
  isMessageObject(val) ? <FormattedMessage message={val} /> : val

/**
 * Flexible base material field component. Provide some `InputComponent`
 * react component to render something inside the field, like input or
 * select or anything you want.
 * @param  {Object} props
 * @return {React.Element}
 */

function BaseField(props: Props) {
  const handleClearClick = () => {
    const {
      input: { onChange },
      onClickClear,
      emptyValue,
      disabled,
    } = props

    if (!disabled) {
      if (onClickClear) {
        onClickClear(emptyValue)
      } else if (onChange) {
        onChange(emptyValue)
      }
    }
  }

  const {
    containerRef,
    innerRef,
    input,
    meta,
    type,
    label,
    disabled,
    disableFloatingLabel,
    emptyValueComparator,
    appearance: customAppearance,
    selectable,
    cleanable,
    hideErrors,
    renderPrefix,
    renderPostfix,
    renderInput,
    emptyValue,
  }: Props = props

  const nonEmpty = emptyValueComparator
    ? emptyValueComparator(input.value, emptyValue)
    : !isNil(input.value)

  const actualAppearance = useAppearance('BASE_FIELD', customAppearance)

  const styleProps: Styles = {
    $palette: () => null,
    $appearance: actualAppearance,
    $haveLabel: !!label,
    $focus: !!meta.active,
    $nonEmpty: nonEmpty,
    $invalid: !!(meta.touched && (!!meta.error || !!meta.warning)),
    $loading: !!(meta.loading || meta.asyncValidating),
    $disabled: !!disabled || !!meta.locked,
    $disableFloatingLabel: !!disableFloatingLabel,
    $alwaysShowPlaceholder: !!disableFloatingLabel,
    $cleanable: cleanable && nonEmpty && 'emptyValue' in props,
    $hasPrefix: !!renderPrefix,
    $hidden: false,
  }
  styleProps.$hidden = !(
    styleProps.$focus ||
    styleProps.$nonEmpty ||
    !styleProps.$haveLabel ||
    styleProps.$alwaysShowPlaceholder ||
    !(actualAppearance || '').startsWith('material')
  )

  const reallySelectable = selectable || type === 'select'
  const haveControls = !!(
    styleProps.$loading ||
    styleProps.$cleanable ||
    reallySelectable
  )

  const prefixElem = renderPrefix && renderPrefix(styleProps)
  const postfixElem = renderPostfix && renderPostfix(styleProps)
  const inputElem =
    renderInput &&
    renderInput({
      props,
      style: styleProps,
      meta,
      input,
      innerRef,
    })

  return (
    <Container ref={containerRef} {...styleProps}>
      <Content {...styleProps}>
        {!!label && <Label {...styleProps}>{label}</Label>}
        <InputWithControls {...styleProps}>
          <InputWrapper {...styleProps}>
            {prefixElem && (
              <PrefixWrapper {...styleProps} onClick={input?.onClick}>
                {prefixElem}
              </PrefixWrapper>
            )}
            {inputElem}
            {(postfixElem || haveControls) && (
              <PostfixWrapper {...styleProps}>
                {postfixElem}
                {styleProps.$cleanable && (
                  <ClearButton
                    {...styleProps}
                    onClick={handleClearClick}
                    onMouseDown={preventDefault}
                  />
                )}
                {reallySelectable && <ArrowButton onClick={input?.onClick} />}
                {styleProps.$loading && (
                  <LoadingContainer>
                    <LoadingSpinner />
                  </LoadingContainer>
                )}
              </PostfixWrapper>
            )}
          </InputWrapper>
        </InputWithControls>
      </Content>
      <Baseline {...styleProps} />
      {meta.touched && !hideErrors && (
        <Errors {...styleProps}>
          {!!meta.error && <div>{getTranslatedValue(meta.error)}</div>}
          {!!meta.warning && <div>{getTranslatedValue(meta.warning)}</div>}
        </Errors>
      )}
    </Container>
  )
}

BaseField.defaultProps = {
  renderInput: () => null,
  disabled: false,
  disableFloatingLabel: false,
  cleanable: true,
  input: {},
  meta: {},
}

export default BaseField
